﻿module Sm.UI.Menu

open MobF

open Sm.Service
open Sm.Service.App
open Sm.Service.Session
open Sm.Service.Remote.Synchronizer

open Sm.UI.Form

type Action =
    | Export
    | Import
    | ToggleAutoFocus
    | Logout

type ActionMenuItem = {
    Label: string
    Action: Action
    Icon: IconName option
}

type MenuItem =
    | Separator
    | Header of string
    | Status of string * IconName option
    | Action of ActionMenuItem
    | ModalInput of ActionMenuItem * TextInput.Type

type State = {
    IsOpen: bool
    Input: (Action * TextInput.Type) option
}

type Msg =
    | Open
    | Close
    | Handle of Action
    | EndInput

type IComputed =
    abstract Items: MenuItem list

type Type = Model<State, Msg, IComputed>

let create ctx (session: SessionModel) : Type =
    let init () =
        {
            IsOpen = false
            Input = None
        }

    let update (state, post) = function
        | Open ->
            { state with State.IsOpen = true }

        | Close ->
            match state.Input with
            | None -> { state with IsOpen = false }
            | Some _ -> state

        | Handle action ->
            match action with
            | Export ->
                promise {
                    let! data = Backup.export ctx
                    do! BrowserNative.saveExport data
                }
                |> Promise.start
                state

            | Import ->
                promise {
                    match! BrowserNative.loadExport () with
                    | None -> ()
                    | Some data -> do! Backup.import data ctx
                }
                |> Promise.start
                state

            | ToggleAutoFocus ->
                let setting = Settings.current
                let value = Some (not setting.Computed.AutoFocus)
                setting.Post(Settings.SetAutoFocus value)
                state

            | Logout ->
                session.Post(Session.Logout)
                state

        | EndInput ->
            post Close
            { state with Input = None }

    let compute _ =
        { new IComputed with
            member _.Items =
                [
                    Header ctx.User.Name

                    match ctx.Synchronizer.Computed.Status with
                    | Pending | Status.Offline -> Status ("Offline", Some Offline)
                    | UpToDate _ -> Status ("Online", Some Sync)
                    | Running Preparing -> Status ("Syncing..", Some SyncDown)
                    | Running (Receiving _) -> Status ("Receiving..", Some SyncDown)
                    | Running (Sending _) -> Status ("Sending..", Some SyncUp)
                    | HaltedSince _ -> Status ("Unable to sync", Some Warning)
                    | Synchronized -> Status ("All changes synced", Some SyncOk)

#if DEBUG
                    Separator
                    Action {
                        Label = "Export Data"
                        Action = Export
                        Icon = Some Box
                    }

                    Action {
                        Label = "Import Data"
                        Action = Import
                        Icon = None
                    }
#endif

                    Separator
                    Action {
                        Label = "Auto Focus Step Input"
                        Action = ToggleAutoFocus
                        Icon =
                            if Settings.current.Computed.AutoFocus then Some Tick
                            else None
                    }

                    Separator
                    Action {
                        Label = "Logout"
                        Action = Logout
                        Icon = Some Zzz
                    }
                ]
        }

    Model.useInit init
    |> Model.andUpdateWithPost update
    |> Model.createWithComputed compute
